// Reusable "sync your schedule to any calendar app" control.
// Fetches a persistent per-user .ics subscription URL and shows copy + per-app instructions.
// Works for admin, employee, and client portals (the backend scopes events by the caller's role).
import { useState } from 'react';
import apiClient from '@lib/api/client';
import { BottomSheet } from '@/components/common/BottomSheet';

interface SubscribeData {
  token: string;
  webcalUrl: string;
  httpsUrl: string;
}

export function CalendarSubscribe({ compact = false }: { compact?: boolean }) {
  const [data, setData] = useState<SubscribeData | null>(null);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState<string>('');

  const load = async () => {
    setOpen(true);
    if (data) return;
    setLoading(true);
    setError('');
    try {
      const r = await apiClient.get<{ success: boolean; data: SubscribeData }>('/calendar-export/subscribe');
      setData(r.data.data);
    } catch {
      setError('Could not generate your calendar link. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const copy = async (value: string, label: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(label);
      setTimeout(() => setCopied(''), 1500);
    } catch { /* clipboard unavailable */ }
  };

  return (
    <div>
      <button
        onClick={load}
        className={`flex items-center gap-1.5 font-medium text-ochre-600 hover:text-ochre-800 ${compact ? 'text-xs' : 'text-sm'}`}
      >
        <span className="material-symbols-outlined text-base">calendar_add_on</span>
        Sync to my calendar
      </button>

      <BottomSheet isOpen={open} onClose={() => setOpen(false)} title="Sync your schedule" maxWidth="md">
            {loading && <div className="h-20 bg-gray-100 animate-pulse rounded-lg" />}
            {error && <p className="text-sm text-red-600">{error}</p>}

            {data && (
              <div className="space-y-4">
                <p className="text-sm text-gray-600">
                  Subscribe once and your OchrePoint jobs stay up to date automatically in any calendar app.
                </p>

                <div className="space-y-2">
                  <label className="text-xs font-semibold text-gray-500 uppercase">Subscription link</label>
                  <div className="flex gap-2">
                    <code className="flex-1 min-w-0 truncate bg-gray-100 rounded-lg px-3 py-2 text-xs text-gray-700">{data.httpsUrl}</code>
                    <button onClick={() => copy(data.httpsUrl, 'https')}
                      className="bg-ochre-600 text-white rounded-lg px-3 py-2 text-xs font-medium hover:bg-ochre-700 whitespace-nowrap">
                      {copied === 'https' ? 'Copied!' : 'Copy'}
                    </button>
                  </div>
                </div>

                <div className="space-y-1.5 text-sm text-gray-600">
                  <p className="font-medium text-gray-800">How to add it:</p>
                  <p><span className="font-medium">Apple / iCloud:</span>{' '}
                    <a href={data.webcalUrl} className="text-ochre-600 hover:underline">tap to subscribe</a>, or
                    Settings → Calendar → Add Subscribed Calendar → paste the link.</p>
                  <p><span className="font-medium">Google Calendar:</span> Other calendars → From URL → paste the link.</p>
                  <p><span className="font-medium">Outlook / Microsoft:</span> Add calendar → Subscribe from web → paste the link.</p>
                </div>

                <p className="text-xs text-gray-400">
                  Keep this link private — anyone with it can see your schedule. It refreshes automatically.
                </p>
              </div>
            )}
      </BottomSheet>
    </div>
  );
}
